<script setup lang="ts">
import { ref, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { usePasskey } from '@/composables/usePasskey'
import LanguageDropdown from '@/components/LanguageDropdown.vue'
import { BaseButton, BaseCheckbox, BaseInput } from '@/components/base'

const router = useRouter()
const authStore = useAuthStore()
const { t } = useI18n()
const { locale } = useI18n()
const { isLoading: isPasskeyLoading, error: passkeyError, isSupported: isPasskeySupported, loginWithPasskey } = usePasskey()

const email = ref('')
const password = ref('')
const rememberMe = ref(false)
const selectedLanguage = ref<string | null>(locale.value)
const isLoading = ref(false)
const error = ref('')
const fieldErrors = ref<Record<string, string[]>>({})
const showPassword = ref(false)

// 2FA state
const step = ref<'login' | 'otp'>('login')
const otpId = ref<number | null>(null)
const otpEmail = ref('')
const otpCode = ref('')
const isResending = ref(false)
const resendSuccess = ref(false)
const trustedDeviceEmail = ref(localStorage.getItem('trusted_device_email'))
const retryAfterSeconds = ref<number | null>(null)
let retryCountdownInterval: ReturnType<typeof setInterval> | null = null

const startRetryCountdown = (seconds: number) => {
  if (retryCountdownInterval) clearInterval(retryCountdownInterval)
  retryAfterSeconds.value = seconds
  retryCountdownInterval = setInterval(() => {
    if (retryAfterSeconds.value !== null && retryAfterSeconds.value > 1) {
      retryAfterSeconds.value--
    } else {
      retryAfterSeconds.value = null
      clearInterval(retryCountdownInterval!)
      retryCountdownInterval = null
      error.value = ''
    }
  }, 1000)
}

onUnmounted(() => {
  if (retryCountdownInterval) clearInterval(retryCountdownInterval)
})

const handleLogin = async () => {
  error.value = ''
  fieldErrors.value = {}

  isLoading.value = true

  const result = await authStore.loginWithAPI(email.value, password.value, rememberMe.value, selectedLanguage.value ?? 'de')

  isLoading.value = false

  if (result.success) {
    // Determine language to update
    let languageToUpdate = selectedLanguage.value

    // Update language if selected or pending
    if (languageToUpdate && languageToUpdate !== 'en') {
      try {
        await authStore.updateLanguage(languageToUpdate)
      } catch (err) {
        console.error('Failed to update language:', err)
        // Continue with login even if language update fails
      }
    }
    const u = authStore.user
    if (u?.role === 'reception') {
      router.push('/rcp/terminal')
    } else {
      router.push('/')
    }
  } else if (result.requires_otp && result.otp_id) {
    otpId.value = result.otp_id
    otpEmail.value = result.email || email.value
    step.value = 'otp'
    startRetryCountdown(60)
  } else {
    if (result.errors) {
      fieldErrors.value = result.errors
      const firstErrorField = Object.keys(result.errors)[0]
      const firstError = result.errors[firstErrorField][0]
      error.value = firstError
    } else if (result.message === 'invalid_credentials') {
      error.value = t('auth.login.invalidCredentials')
    } else if (result.message) {
      error.value = result.message
    } else {
      error.value = t('auth.login.loginFailed')
    }
  }
}

const handleVerifyOTP = async () => {
  if (!otpId.value) return
  error.value = ''
  isLoading.value = true

  const result = await authStore.verifyOTP(otpId.value, otpCode.value, rememberMe.value, otpEmail.value, selectedLanguage.value ?? 'de')

  isLoading.value = false

  if (result.success) {
    // refresh trusted device banner state
    trustedDeviceEmail.value = localStorage.getItem('trusted_device_email')

    let languageToUpdate = selectedLanguage.value
    if (languageToUpdate && languageToUpdate !== 'en') {
      try {
        await authStore.updateLanguage(languageToUpdate)
      } catch (err) {
        console.error('Failed to update language:', err)
      }
    }
     const u = authStore.user
    if (u?.role == 'reception') {
      router.push('/rcp/terminal')
    } else {
      router.push('/')
    }
  } else {
    if (result.errors) {
      const firstErrorField = Object.keys(result.errors)[0]
      error.value = result.errors[firstErrorField][0]
    } else {
      error.value = result.message || t('auth.otp.verificationFailed')
    }
  }
}

const handleResendOTP = async () => {
  if (!otpId.value || isResending.value) return
  error.value = ''
  isResending.value = true

  const result = await authStore.resendOTP(otpId.value, selectedLanguage.value ?? 'de')

  isResending.value = false

  if (result.success && result.otp_id) {
    otpId.value = result.otp_id
    resendSuccess.value = true
    setTimeout(() => { resendSuccess.value = false }, 3000)
    startRetryCountdown(60)
  } else if (!result.success) {
    error.value = result.message || t('auth.otp.verificationFailed')
    if (result.retry_after_seconds) {
      startRetryCountdown(result.retry_after_seconds)
    }
  }
}

const backToLogin = () => {
  step.value = 'login'
  otpCode.value = ''
  otpId.value = null
  otpEmail.value = ''
  error.value = ''
}

const handlePasskeyLogin = async () => {
  error.value = ''
  fieldErrors.value = {}

  const result = await loginWithPasskey(email.value || undefined, selectedLanguage.value ?? 'de')

  if (result.cancelled) return

  if (result.success && result.token) {
    await authStore.autoLoginWithToken(result.token)
     const u = authStore.user
    if (u?.role === 'reception') {
      router.push('/rcp/terminal')
    } else {
      router.push('/')
    }
  } else {
    error.value = passkeyError.value || t('passkey.loginFailed')
  }
}

// QR login is only useful from an installed app (its whole purpose is to log in
// within the app's own storage context). Hide it in a regular browser tab.
// display-mode:standalone covers Android/Chrome and iOS 16.4+; navigator.standalone
// is the legacy flag for older iOS home-screen web apps.
const isStandalone =
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as any).standalone === true

const handleQrLogin = () => {
  router.push('/rcp/scan')
}

const today = new Date()
const year = today.getFullYear()
</script>

<template>
  <div class="min-h-screen flex items-center justify-center px-4 py-10">
    <div class="w-full max-w-md mb-8">
      <!-- Card -->
      <div class="bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div class="flex justify-center items-center gap-4 px-8 pt-8">
          <img
              src="/Flixorder_Web_Logo.png"
              alt="Flixorder"
              class="h-10 w-auto object-contain"
            />
        </div>
        <!-- Form -->
        <div v-if="step === 'login'" class="px-8 py-10">
          <!-- Language Selector -->
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-bold text-gray-900">{{ t('auth.login.title') }}</h2>
            <LanguageDropdown v-model="selectedLanguage" />
          </div>

          <!-- Trusted device banner -->
          <div v-if="trustedDeviceEmail" class="mb-5 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <svg class="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <p class="text-green-700 text-sm">{{ t('auth.login.trustedDevice', { email: trustedDeviceEmail }) }}</p>
          </div>

          <!-- Error alert -->
          <div v-if="error" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-red-700 text-sm font-medium">{{ error }}</p>
          </div>

          <!-- Email Input -->
          <div class="mb-4">
            <label class="block text-sm font-semibold text-gray-700 mb-1">{{ t('auth.login.emailLabel') }}</label>
            <BaseInput
              v-model="email"
              type="email"
              :placeholder="t('auth.login.emailPlaceholder')"
              @keyup.enter="handleLogin"
              :invalid="!!(fieldErrors['email'])"
            />
            <p v-if="fieldErrors['email'] && fieldErrors['email'].length > 0" class="mt-1 text-sm text-red-600">
              {{ fieldErrors['email'][0] }}
            </p>
          </div>

          <!-- Password Input -->
          <div class="mb-4">
            <div class="flex justify-between items-center px-1">
              <label class="text-sm font-semibold text-gray-700 mb-1" for="password">{{ t('auth.login.passwordLabel') }}</label>
              <RouterLink to="/forgot-password" class="text-sm text-primary-600 hover:text-primary-700 font-medium">
                {{ t('auth.login.forgotPassword') }}
              </RouterLink>
              
            </div>
            <div class="relative">
              <BaseInput
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="t('auth.login.passwordPlaceholder')"
                @keyup.enter="handleLogin"
                :invalid="!!(fieldErrors['password'])"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-2 text-gray-600 hover:text-gray-900"
              >
                <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z"/>
                  <circle cx="12" cy="12" r="3"/>
                  <line x1="3" y1="21" x2="21" y2="3"/>
                </svg>
              </button>
              <p v-if="fieldErrors['password'] && fieldErrors['password'].length > 0" class="mt-1 text-sm text-red-600">
                {{ fieldErrors['password'][0] }}
              </p>
            </div>
          </div>

          <div class="mb-4">
            <label class="flex items-center gap-3 cursor-pointer">
              <BaseCheckbox v-model="rememberMe"  />
              <span class="text-sm font-bold text-gray-900">{{ t('auth.login.rememberMe') }}</span>
            </label>
          </div>

          <!-- Login Button -->
          <BaseButton
            @click="handleLogin"
            :disabled="isLoading"
            block
          >
            <span v-if="!isLoading">{{ t('auth.login.signInButton') }}</span>
            <span v-else class="flex items-center justify-center gap-2">
              <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {{ t('auth.login.signingIn') }}
            </span>
          </BaseButton>
          <!-- Passkey Login -->
          <div v-if="isPasskeySupported()" class="mt-4">
            <div class="relative flex items-center">
              <div class="flex-grow border-t border-gray-200"></div>
              <span class="mx-3 text-xs text-gray-400 uppercase tracking-wide">{{ t('common.or') }}</span>
              <div class="flex-grow border-t border-gray-200"></div>
            </div>
            <BaseButton
              @click="handlePasskeyLogin"
              :disabled="isPasskeyLoading || isLoading"
              block
              variant="secondary"
              class="mt-4"
            >
              <svg v-if="!isPasskeyLoading" class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <svg v-else class="animate-spin w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>{{ isPasskeyLoading ? t('passkey.loggingIn') : t('passkey.loginButton') }}</span>
            </BaseButton>
          </div>

          <!-- Reception QR Login (only in an installed PWA) -->
          <BaseButton
            v-if="isStandalone"
            @click="handleQrLogin"
            block
            variant="secondary"
            class="mt-4"
          >
            <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4h4v4H4V4zM4 16h4v4H4v-4zM16 4h4v4h-4V4zM14 14h2v2h-2v-2zM18 14h2v2h-2v-2zM14 18h2v2h-2v-2zM18 18h2v2h-2v-2z" />
            </svg>
            <span>{{ t('rcp.scanLogin.loginButton') }}</span>
          </BaseButton>

          <!-- Sign Up Link -->
          <div class="mt-6 text-center border-t border-gray-200 pt-6">
            <p class="text-sm text-gray-600">
              {{ t('auth.login.noAccount') }}
              <RouterLink to="/signup" class="text-primary-600 hover:text-primary-700 font-medium">
                {{ t('auth.login.signUp') }}
              </RouterLink>
            </p>
          </div>
        </div>

        <!-- ── OTP STEP ── -->
        <div v-else-if="step === 'otp'" class="px-8 py-10">
          <div class="flex justify-between items-center mb-8">
            <h2 class="text-2xl font-bold text-gray-900">{{ t('auth.otp.title') }}</h2>
            <LanguageDropdown v-model="selectedLanguage" />
          </div>

          <!-- Subtitle -->
          <p class="text-sm text-gray-600 mb-6">
            {{ t('auth.otp.subtitle') }}
            <span class="font-semibold text-gray-900">{{ otpEmail }}</span>
          </p>

          <!-- Error alert -->
          <div v-if="error" class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-red-700 text-sm font-medium">{{ error }}</p>
          </div>

          <!-- Resend success -->
          <div v-if="resendSuccess" class="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
            <p class="text-primary-700 text-sm">{{ t('auth.otp.codeSent') }}</p>
          </div>

          <!-- Code input -->
          <div class="mb-8">
            <label class="block text-sm font-semibold text-gray-700 mb-2">{{ t('auth.otp.codeLabel') }}</label>
            <BaseInput
              v-model="otpCode"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength="6"
              :placeholder="t('auth.otp.codePlaceholder')"
              @keyup.enter="handleVerifyOTP"
              @input="otpCode = otpCode.replace(/[^0-9]/g, '')"
              class="text-center text-2xl tracking-widest font-mono"
            />
          </div>

          <!-- Verify button -->
          <BaseButton
            @click="handleVerifyOTP"
            :disabled="isLoading || otpCode.length < 6"
            block
          >
            <span v-if="!isLoading">{{ t('auth.otp.verifyButton') }}</span>
            <span v-else class="flex items-center justify-center gap-2">
              <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {{ t('auth.otp.verifying') }}
            </span>
          </BaseButton>

          <!-- Resend + back -->
          <div class="mt-5 flex items-center justify-between">
            <button
              type="button"
              @click="handleResendOTP"
              :disabled="isResending || retryAfterSeconds !== null"
              class="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isResending">{{ t('auth.otp.resending') }}</span>
              <span v-else-if="retryAfterSeconds !== null">{{ t('auth.otp.resendCode') }} ({{ retryAfterSeconds }}s)</span>
              <span v-else>{{ t('auth.otp.resendCode') }}</span>
            </button>
            <button
              type="button"
              @click="backToLogin"
              class="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              {{ t('auth.otp.backToLogin') }}
            </button>
          </div>
        </div>

      </div>

      <!-- Footer -->
      <p class="text-center text-gray-400 text-xs mt-8">© {{ year }} Flixorder. {{ t('common.allRightsReserved') }}</p>
    </div>
  </div>
</template>

<style scoped></style>
